import React, { useCallback, useMemo } from "react"

import { ChartFigure } from "@coval/ui/chart-figure"
import {
  Bar,
  BarChart,
  Cell,
  Customized,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { PRIMARY_BAR_SERIES_COLOR } from "@/components/charts/seriesPalette"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  buildDistributionTable,
  summarizeDistribution,
} from "./DistributionFigure"
import {
  buildStatLines,
  computeDistributionStats,
  useStatsToggle,
  type DistributionStatKey,
  type DistributionStats,
} from "./distributionStatsUtils"
import { createHistogramBins, formatBinRange } from "./HistogramChart"
import { HistogramDistributionStats } from "./HistogramDistributionStats"

interface MetricDistributionChartProps {
  metricOutputs: RunMetricOutput[]
  showChart?: boolean
  metricRecord: {
    aggregate_units?: string | null
    metric_display_name: string
  }
  extractFloatValues?: (metricOutputs: RunMetricOutput[]) => number[]
  formatStatValue?: (v: number) => string
  /** When provided, highlight the bar containing this value and dim all others. */
  highlightValue?: number | string | null
  /** Called when a bar is clicked to filter by its range/value. Passes null to clear. */
  onBarClick?: (filter: ChartBarFilter) => void
  /** The currently active bar filters, used to show active state. A bar is active if ALL filters match (AND logic). */
  activeFilters?: ChartBarFilter[]
}

interface CategoricalDataPoint {
  label: string
  count: number
}

export const MetricDistributionChart: React.FC<
  MetricDistributionChartProps
> = ({
  metricOutputs,
  showChart = true,
  metricRecord,
  extractFloatValues,
  formatStatValue,
  highlightValue,
  onBarClick,
  activeFilters,
}) => {
  const units = metricRecord.aggregate_units || ""

  const chartData = useMemo(() => {
    if (metricOutputs.length === 0) {
      return null
    }

    const outputType = metricOutputs[0]?.output_type

    if (outputType === "float") {
      // Extract float values — use custom extractor when provided (e.g. for percent scaling)
      const floatValues = extractFloatValues
        ? extractFloatValues(metricOutputs)
        : metricOutputs
            .map((mo) => mo.float_value)
            .filter((val): val is number => val !== null)

      if (floatValues.length === 0) {
        return null
      }

      const bins = createHistogramBins(floatValues)
      const dataMin = bins.length > 0 ? bins[0].min : 0
      const dataMax = bins.length > 0 ? bins[bins.length - 1].max : dataMin + 1
      return {
        type: "histogram" as const,
        data: bins.map((bin) => ({
          label: bin.label,
          count: bin.count,
          min: bin.min,
          max: bin.max,
          x: (bin.min + bin.max) / 2,
        })),
        totalDataPoints: floatValues.length,
        rawFloatValues: floatValues,
        domain: [dataMin, dataMax],
      }
    } else if (outputType === "string") {
      // Count occurrences of each string value
      const valueCounts = metricOutputs.reduce(
        (acc: Record<string, number>, curr) => {
          if (curr.string_value) {
            const normalizedValue = curr.string_value.trim()
            acc[normalizedValue] = (acc[normalizedValue] || 0) + 1
          }
          return acc
        },
        {},
      )

      const data: CategoricalDataPoint[] = Object.entries(valueCounts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count) // Sort by count descending
        .map(({ value, count }) => ({
          label: value,
          count,
        }))

      return {
        type: "categorical" as const,
        data,
        totalDataPoints: data.reduce((sum, d) => sum + d.count, 0),
      }
    } else if (outputType === "set") {
      // For set values, count occurrences of each set length or individual items
      const valueCounts = metricOutputs.reduce(
        (acc: Record<string, number>, curr) => {
          if (curr.set_value && Array.isArray(curr.set_value)) {
            // Count by set length
            const length = curr.set_value.length
            const key = length > 0 ? `Length: ${length}` : "Empty"
            acc[key] = (acc[key] || 0) + 1
          } else {
            acc["No data"] = (acc["No data"] || 0) + 1
          }
          return acc
        },
        {},
      )

      const data: CategoricalDataPoint[] = Object.entries(valueCounts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count) // Sort by count descending
        .map(({ value, count }) => ({
          label: value,
          count,
        }))

      return {
        type: "categorical" as const,
        data,
        totalDataPoints: data.reduce((sum, d) => sum + d.count, 0),
      }
    }

    return null
  }, [metricOutputs, extractFloatValues])

  const isBarActive = useCallback(
    (
      entry: { min?: number; max?: number; label?: string },
      _index: number,
    ): boolean => {
      if (!activeFilters || activeFilters.length === 0) {
        return false
      }
      return activeFilters.some((filter) => {
        switch (filter.type) {
          case "range":
            return (
              entry.min != null &&
              entry.max != null &&
              entry.min === filter.min &&
              entry.max === filter.max
            )
          case "exact":
            return entry.label != null && entry.label === filter.value
          case "gte":
            return entry.max != null && entry.max >= filter.value
          case "gt":
            return entry.max != null && entry.max > filter.value
          case "lte":
            return entry.min != null && entry.min <= filter.value
          case "lt":
            return entry.min != null && entry.min < filter.value
          default:
            return false
        }
      })
    },
    [activeFilters],
  )

  const handleBarClick = useCallback(
    (entry: { min?: number; max?: number; label?: string }) => {
      if (!onBarClick) {
        return
      }
      // Histogram bin
      if (entry.min != null && entry.max != null) {
        onBarClick({ type: "range", min: entry.min, max: entry.max })
        return
      }
      // Categorical bar
      if (entry.label != null) {
        onBarClick({ type: "exact", value: entry.label })
      }
    },
    [onBarClick],
  )

  const highlightedBinIndex = useMemo(() => {
    if (highlightValue == null || !chartData) {
      return null
    }

    if (chartData.type === "histogram" && typeof highlightValue === "number") {
      const bins = chartData.data as Array<{ min: number; max: number }>
      const lastIndex = bins.length - 1
      const idx = bins.findIndex((bin, i) =>
        i === lastIndex
          ? highlightValue >= bin.min && highlightValue <= bin.max
          : highlightValue >= bin.min && highlightValue < bin.max,
      )
      return idx >= 0 ? idx : null
    }

    if (chartData.type === "categorical") {
      const idx = chartData.data.findIndex(
        (d) => d.label === String(highlightValue),
      )
      return idx >= 0 ? idx : null
    }

    return null
  }, [chartData, highlightValue])

  const distributionStats = useMemo(() => {
    if (
      chartData?.type !== "histogram" ||
      !("rawFloatValues" in chartData) ||
      !chartData.rawFloatValues?.length
    ) {
      return null
    }
    return computeDistributionStats(chartData.rawFloatValues)
  }, [chartData])

  const { visibleStats, setHoveredStat, handleToggleStat, statsToShow } =
    useStatsToggle()

  // Calculate explicit Y-axis ticks to ensure grid lines align with labels
  const yAxisTicks = useMemo(() => {
    if (!chartData || chartData.data.length === 0) {
      return [0]
    }
    const maxCount = Math.max(...chartData.data.map((d) => d.count))
    if (maxCount <= 0) {
      return [0]
    }
    // Target approximately 5-7 ticks for detail view
    const targetTickCount = Math.min(7, maxCount + 1)
    // Calculate a nice step size (1, 2, 5, 10, 20, etc.)
    const rawStep = maxCount / (targetTickCount - 1)
    const magnitude = 10 ** Math.floor(Math.log10(rawStep))
    const residual = rawStep / magnitude
    let niceStep: number
    if (residual <= 1.5) {
      niceStep = magnitude
    } else if (residual <= 3) {
      niceStep = 2 * magnitude
    } else if (residual <= 7) {
      niceStep = 5 * magnitude
    } else {
      niceStep = 10 * magnitude
    }
    // Ensure step is at least 1 for integer counts
    niceStep = Math.max(1, Math.round(niceStep))
    // Generate ticks from 0 to maxCount
    const ticks: number[] = []
    for (let tick = 0; tick <= maxCount; tick += niceStep) {
      ticks.push(tick)
    }
    // Ensure we include the max value or slightly above if needed
    if (ticks[ticks.length - 1] < maxCount) {
      ticks.push(ticks[ticks.length - 1] + niceStep)
    }
    return ticks
  }, [chartData])

  const renderStatReferenceLines = useCallback(
    (props: {
      offset?: { left: number; top: number; width: number; height: number }
      domain: [number, number]
      distributionStats: DistributionStats
      statsToShow: Set<DistributionStatKey>
    }) => {
      const { offset, domain, distributionStats, statsToShow } = props
      if (
        !offset ||
        offset.width <= 0 ||
        !distributionStats ||
        statsToShow.size === 0
      ) {
        return null
      }
      const [dMin, dMax] = domain
      const span = dMax - dMin || 1
      const valueToX = (value: number) =>
        offset.left + ((value - dMin) / span) * offset.width
      const yTop = offset.top
      const yBottom = offset.top + offset.height
      const lines = buildStatLines(distributionStats, statsToShow, valueToX)
      const MIN_LABEL_SPACING = 42
      const LEVEL_OFFSET = 16
      const labelLevels: number[] = []
      lines.forEach(({ x }, i) => {
        let level = 0
        while (
          labelLevels
            .slice(0, i)
            .some(
              (lvl, j) =>
                lvl === level && Math.abs(lines[j].x - x) < MIN_LABEL_SPACING,
            )
        ) {
          level++
        }
        labelLevels.push(level)
      })
      return (
        <g className="recharts-reference-line-lines">
          {lines.map(({ x, stroke, label }, index) => (
            <g key={label}>
              <line
                x1={x}
                y1={yTop}
                x2={x}
                y2={yBottom}
                stroke={stroke}
                strokeDasharray="2 2"
                strokeWidth={1.5}
              />
              <text
                x={x}
                y={yTop - 4 - labelLevels[index] * LEVEL_OFFSET}
                textAnchor="middle"
                fontSize={14}
                fill={stroke}
              >
                {label}
              </text>
            </g>
          ))}
        </g>
      )
    },
    [],
  )

  const _formatStatValue = formatStatValue ?? ((v: number) => v.toFixed(2))

  const figureRows = useMemo(
    () =>
      chartData?.type === "histogram"
        ? chartData.data.map((bin) => ({
            label: formatBinRange(
              bin.min,
              bin.max,
              bin.label,
              units || undefined,
            ),
            count: bin.count,
          }))
        : (chartData?.data ?? []).map((entry) => ({
            label: entry.label,
            count: entry.count,
          })),
    [chartData, units],
  )

  if (!showChart) {
    if (!distributionStats) {
      return (
        <p className="text-coval-text-muted">
          No evaluated metric data available.
        </p>
      )
    }
    return (
      <div className="@container/stats">
        <HistogramDistributionStats
          className="grid-cols-2 @lg/stats:grid-cols-4"
          stats={distributionStats}
          formatValue={_formatStatValue}
          units={units || undefined}
        />
      </div>
    )
  }

  if (!chartData || chartData.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-coval-text-muted">
        No data available for distribution
      </div>
    )
  }

  if (chartData.type === "histogram" && chartData.data.length <= 1) {
    return (
      <div className="w-full mt-4">
        {distributionStats && (
          <div className="mb-4">
            <HistogramDistributionStats
              stats={distributionStats}
              formatValue={_formatStatValue}
              units={units || undefined}
              visibleStats={visibleStats}
              onToggleStat={handleToggleStat}
              onHoverStat={setHoveredStat}
            />
          </div>
        )}
        <p className="text-base text-coval-text-muted mt-2">
          Not enough variance to display distribution.
        </p>
      </div>
    )
  }

  return (
    <div className="w-full mt-4">
      {chartData.type === "histogram" && distributionStats && (
        <div className="mb-4">
          <HistogramDistributionStats
            stats={distributionStats}
            formatValue={_formatStatValue}
            units={units || undefined}
            visibleStats={visibleStats}
            onToggleStat={handleToggleStat}
            onHoverStat={setHoveredStat}
          />
          <p className="text-xs text-coval-text-muted mt-2">
            Click a stat to show or hide its reference line on the chart.
          </p>
        </div>
      )}
      <ChartFigure
        label={`Distribution of ${metricRecord.metric_display_name}`}
        summary={summarizeDistribution(figureRows, highlightedBinIndex)}
        table={buildDistributionTable(figureRows, {
          valueHeader: units
            ? `${metricRecord.metric_display_name} (${units})`
            : metricRecord.metric_display_name,
          highlightIndex: highlightedBinIndex,
          onFilter: onBarClick
            ? (index) => handleBarClick(chartData.data[index])
            : undefined,
          isFilterActive: (index) => isBarActive(chartData.data[index], index),
        })}
        style={{ width: "100%", height: "320px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            role="img"
            aria-label={`Distribution of ${metricRecord.metric_display_name}`}
            data={chartData.data}
            margin={{ top: 40, right: 16, left: 16, bottom: 10 }}
            barCategoryGap="8%"
            barGap={0}
          >
            {/* Draw horizontal grid lines at each Y-axis tick for perfect alignment */}
            {yAxisTicks.map((tick) => (
              <ReferenceLine
                key={tick}
                y={tick}
                stroke="var(--coval-border)"
                strokeWidth={1}
              />
            ))}
            <XAxis
              dataKey={chartData.type === "histogram" ? "x" : "label"}
              type={chartData.type === "histogram" ? "number" : undefined}
              domain={
                chartData.type === "histogram" ? chartData.domain : undefined
              }
              angle={chartData.type === "histogram" ? 0 : -45}
              textAnchor={chartData.type === "histogram" ? "middle" : "end"}
              height={chartData.type === "histogram" ? 50 : 100}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "var(--coval-border)" }}
              interval={0}
              label={{
                value: units
                  ? `${metricRecord.metric_display_name} (${units})`
                  : metricRecord.metric_display_name,
                position: "insideBottom",
                offset: -5,
                style: {
                  textAnchor: "middle",
                  fontSize: 11,
                  fontWeight: 600,
                  fill: "var(--coval-text)",
                },
              }}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "var(--coval-border)" }}
              width={30}
              domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              allowDecimals={false}
              label={{
                value: "Frequency (count)",
                angle: -90,
                position: "insideLeft",
                offset: 2,
                dx: -10,
                style: {
                  textAnchor: "middle",
                  fontSize: 11,
                  fontWeight: 600,
                  fill: "var(--coval-text)",
                },
              }}
            />
            <Tooltip
              formatter={(value: number) => [value, "Count"]}
              labelFormatter={(label, payload) => {
                if (chartData.type === "histogram" && payload?.[0]?.payload) {
                  const {
                    min,
                    max,
                    label: binLabel,
                  } = payload[0].payload as {
                    min: number
                    max: number
                    label: string
                  }
                  return formatBinRange(min, max, binLabel, units || undefined)
                }
                return `Value: ${label}`
              }}
              contentStyle={{
                borderRadius: "6px",
                fontSize: "12px",
                padding: "4px 8px",
                backgroundColor: "var(--surface-default)",
                border: "1px solid var(--coval-border)",
                color: "var(--coval-text)",
              }}
              labelStyle={{
                color: "var(--coval-text)",
              }}
              itemStyle={{
                color: "var(--coval-text)",
              }}
              cursor={{ fill: "var(--coval-border)" }}
            />
            <Bar
              dataKey="count"
              fill={PRIMARY_BAR_SERIES_COLOR}
              radius={[2, 2, 0, 0]}
              onClick={
                onBarClick
                  ? (_data: unknown, index: number) =>
                      handleBarClick(chartData.data[index])
                  : undefined
              }
              style={onBarClick ? { cursor: "pointer" } : undefined}
            >
              {chartData.data.map((entry, index) => {
                const active = isBarActive(entry, index)
                const hasActiveFilters =
                  activeFilters != null && activeFilters.length > 0
                const hasHighlight = highlightedBinIndex != null
                // Dim logic: dim if there's a highlight/filter and this bar isn't the one
                const dimmed = hasHighlight
                  ? index !== highlightedBinIndex
                  : hasActiveFilters && !active
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={PRIMARY_BAR_SERIES_COLOR}
                    opacity={dimmed ? 0.4 : 1}
                  />
                )
              })}
            </Bar>
            {chartData.type === "histogram" &&
              distributionStats &&
              chartData.domain && (
                <Customized
                  component={renderStatReferenceLines}
                  domain={chartData.domain as [number, number]}
                  distributionStats={distributionStats}
                  statsToShow={statsToShow}
                />
              )}
          </BarChart>
        </ResponsiveContainer>
      </ChartFigure>
    </div>
  )
}
